import { Button } from '@/components/elements/forms/buttons';
import Input from '@/components/elements/forms/Input/Input';
import Icon from '@/components/icons/Icon';
import Modal from '@/components/modules/modals/redesign/Modal/Modal';
import { getAmplitudeClientIds, trackMpEvent } from '@/helpers';
import useMobileView from '@/hooks/useMobileView';
import { _s } from '@/locale';
import { Place } from '@/types/state/shared';
import { useEffect, useState } from 'react';
import { EVENT_NAME, SCREEN_NAME, UNVERIFIED_SUBSCRIPTION_TYPE } from '../constants/tracking';
import { getCompanyTrackingProps } from '../helpers/tracking';
import { StartingPoint } from '../ProfilePage';

const SIGN_UP_STEP = 'company_bankid_verification_initiated';
const baseTranslationKey = 'features.ProfilePages.modules.VerifyCompanyModal';

const VerifyCompanyModal = ({
  onClose,
  place,
  trigger,
}: {
  onClose: () => void;
  place: Place;
  trigger?: StartingPoint;
}) => {
  const { isMobileView } = useMobileView();
  const [organisationNumber, setOrganisationNumber] = useState<string | undefined>(undefined);
  const [lastFour, setLastFour] = useState<string>('');
  const { deviceId, userId } = getAmplitudeClientIds('saas');

  const placeOrganisationNumber = place.about?.orgIds ? place.about?.orgIds[0] : place.about?.orgId;
  const organisationNumberWithoutLastFour = placeOrganisationNumber.slice(0, -4);

  const handleChangeLastFour = (text: string) => {
    const numericValue = text.replace(/[^0-9]/g, '');
    setLastFour(numericValue);
    setOrganisationNumber(organisationNumberWithoutLastFour + numericValue);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    trackMpEvent(
      EVENT_NAME.PROFILE_PAGE_SIGN_UP_FLOW,
      {
        ...getCompanyTrackingProps({
          id: place.id,
          name: place.about?.name,
          subType: UNVERIFIED_SUBSCRIPTION_TYPE,
          step: SIGN_UP_STEP,
          screen: SCREEN_NAME.VERIFY_YOUR_COMPANY,
          startingPoint: trigger,
          selectedPlan: 'not_available',
        }),
      },
      false,
      true,
    );

    e.currentTarget.submit();
  };

  useEffect(() => {
    trackMpEvent(
      EVENT_NAME.SCREEN_SHOWN,
      {
        screen_name: SCREEN_NAME.VERIFY_YOUR_COMPANY,
      },
      false,
      true,
    );
  }, []);

  return (
    <Modal className={`${isMobileView ? 'h-screen' : 'md:max-w-[496px]'}`} isOpen onRequestClose={onClose}>
      <form
        onSubmit={handleSubmit}
        action="https://crm.bddev.se/register-company-page"
        method="POST"
        className="flex h-full items-center">
        <input type="hidden" name="MarketplaceId" value={place.id || ''} />
        <input type="hidden" name="Organisationsnummer" value={organisationNumber || ''} />
        <input type="hidden" name="CompanyName" value={place.about?.name || ''} />
        <input type="hidden" name="City" value={place.contact?.address?.city || ''} />
        <input type="hidden" name="ZipCode" value={place.contact?.address?.zipcode || ''} />
        <input type="hidden" name="Phone" value={place.contact?.phone || ''} />
        <input type="hidden" name="DeviceId" value={deviceId || ''} />
        <input type="hidden" name="UserId" value={userId || ''} />
        <div className={`${isMobileView ? 'flex h-full flex-col' : 'h-[590px]'}`}>
          <Modal.NewHeader onClose={onClose} floating={!isMobileView} title={_s(`${baseTranslationKey}.title`)} />
          <div className={`p-xl gap-xl bg-surface_l4 relative flex flex-col ${isMobileView ? 'flex-1' : 'h-full'}`}>
            <div className="gap-2xl z-10 flex h-full flex-col justify-end">
              <h2 className="text-h-xl font-bold">{'Är du VD eller firmatecknare?'}</h2>
              <p className="text-l pb-xl">{_s(`${baseTranslationKey}.body`, { name: place.about?.name })}</p>
              <div className="gap-sm flex w-full items-center">
                <div className="relative flex-1">
                  <label
                    htmlFor="text-input"
                    className="text-l text-fg_primary absolute -top-6 left-0 whitespace-nowrap">
                    {_s(`${baseTranslationKey}.inputLabel`)}
                  </label>
                  <Input placeholder={organisationNumberWithoutLastFour} disabled />
                </div>
                <span className="flex-shrink-0">{' - '}</span>
                <div className="w-[100px] flex-shrink-0">
                  <Input
                    onChange={(e) => handleChangeLastFour(e.target.value)}
                    placeholder="XXXX"
                    type="text"
                    value={lastFour}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={4}
                  />
                </div>
              </div>
              <Button
                disabled={lastFour?.length !== 4}
                label={_s(`${baseTranslationKey}.cta${isMobileView ? 'Mobile' : ''}`)}
                type="submit"
                size="lg"
                rightIcon={<Icon ext="svg" variant={isMobileView ? 'payment-method-bank-id' : 'arrow-right'} />}
              />
            </div>
            <div className={`absolute left-0 top-0 ${isMobileView ? 'z-0 w-full overflow-hidden' : 'z-0 w-full'}`}>
              <picture>
                <source srcSet={'/images/verify-company-background.png'} media="(min-width: 768px)" />
                <img src={'/images/verify-company-background.png'} alt="" />
              </picture>
            </div>
            <div className="absolute bottom-0 left-0 h-full w-full bg-gradient-to-t from-white to-transparent"></div>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default VerifyCompanyModal;
